import { InputHTMLAttributes, useEffect, useRef, useState } from 'react'
import { IoCheckmark, IoClose } from 'react-icons/io5'
import { cn } from '../lib/utils'

export type EditableTextFieldProps = {
  defaultValue?: string
  onSave?: (value?: string) => void
  onCancel?: () => void
  noBorder?: boolean
} & InputHTMLAttributes<HTMLInputElement>

export const EditableTextField = (props: EditableTextFieldProps) => {
  const {
    defaultValue,
    onChange,
    onSave,
    onCancel,
    onFocus,
    onBlur,
    className,
    noBorder,
    value: valueProp,
    ...rest
  } = props
  const [value, setValue] = useState(valueProp?.toString() || defaultValue)
  const [isEditing, setIsEditing] = useState(false)
  const currentRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setValue(valueProp?.toString() || defaultValue)
  }, [valueProp, defaultValue])

  useEffect(() => {
    // Ensure the component is mounted before focusing
    setTimeout(() => {
      if (rest.autoFocus) {
        setIsEditing(true)
        currentRef.current?.focus()
      }
    }, 0)
  })

  return (
    <div className="relative">
      <div
        className={cn(
          'min-h-[34px] rounded px-1 py-1.5 w-fit cursor-pointer bg-white hover:bg-gray-1 inset-px z-1',
          className,
          isEditing ? 'hidden' : 'absolute',
        )}
        onClick={() => {
          setIsEditing(true)
          currentRef.current?.focus()
        }}
      >
        {value}
      </div>
      <input
        {...rest}
        autoComplete="off"
        ref={currentRef}
        value={value}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSave?.(value)
            e.preventDefault()
            e.stopPropagation()
            e.currentTarget.blur()
          } else if (e.key === 'Escape') {
            setValue(defaultValue)
            onCancel?.()
            e.preventDefault()
            e.stopPropagation()
            e.currentTarget.blur()
          }
        }}
        onChange={(e) => {
          setValue(e.target.value)
          onChange?.(e)
        }}
        onFocus={(e) => {
          setIsEditing(true)
          onFocus?.(e)
        }}
        onBlur={(e) => {
          setIsEditing(false)
          onBlur?.(e)
        }}
        className={cn(
          'peer/editable w-full min-h-[34px] border border-transparent rounded px-1 py-1.5 focus:border-gray-9 outline-none text-ellipsis focus:pe-16',
          className,
          noBorder ? 'border-none' : '',
        )}
      />
      <button
        type="button"
        className="absolute end-[38px] top-[5px] hidden h-6 w-6 items-center justify-center rounded border border-gray-7 text-gray-9 peer-focus/editable:flex"
        onMouseDown={(e) => {
          setValue(defaultValue)
          onCancel?.()
        }}
      >
        <IoClose />
      </button>
      <button
        type="button"
        className="absolute end-[10px] top-[5px] hidden h-6 w-6 items-center justify-center rounded border border-green-9 bg-green-2 text-green-9 peer-focus/editable:flex"
        onMouseDown={(e) => {
          onSave?.(value)
        }}
      >
        <IoCheckmark />
      </button>
    </div>
  )
}
